import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from "@tanstack/react-query";
import type { InfiniteData } from "@tanstack/react-query";
import { queryClient } from "@/config/react-query";
import { useUser } from "@/contexts/user-provider";
import createId from "@/lib/create-id";
import { CallDetail, ChatBase, ChatDetail, ChatEmailSummary, ChatSummary, ChatTaskDraft, UnreadCounts, isOptedOutOfSms } from "@/types/chat";
import { ChatMode, ChatStatus } from "@/types/enums/chat";
import { MessageKind } from "@/types/enums/message";
import { MessageStatus } from "@/types/enums/message";
import { UserType } from "@/types/enums/user-type";
import { type MessageContext, getContextMessageType } from "@/types/message";
import { MessageSummary } from "@/types/message";
import { CursorPage } from "@/types/pagination";
import { ScheduleMessageInput, ScheduledMessageSummary } from "@/types/scheduled-message";
import { requestData } from "@/api/api-config";
import { invalidateOpportunityContactViews } from "@/api/provider/opportunities/opportunity-query-keys";
export interface FetchChatsOptions {
  cursor?: string | null;
  perPage?: number;
  searchTerm?: string;
  controller?: AbortController | null;
  chatId?: string | null;
  patientId?: string;
  unreadOnly?: boolean;
  unrespondedOnly?: boolean;
  locationId?: string;
  humanOnly?: boolean;
}
async function fetchUnreadCount(locationId?: string): Promise<number | null> {
  return await requestData<number | null>("comms/chat/unread-count", "get", {
    searchParams: {
      locationId
    }
  });
}
async function fetchChats(options: FetchChatsOptions): Promise<CursorPage<ChatSummary> | null> {
  const searchParams = new URLSearchParams();
  const paramMap: Record<string, string | undefined> = options.patientId ? {
    patientId: options.patientId
  } : {
    cursor: options.cursor ?? undefined,
    perPage: options.perPage?.toString(),
    searchTerm: options.searchTerm || "",
    chatId: options.chatId ?? undefined,
    locationId: options.locationId,
    unreadOnly: options.unreadOnly?.toString(),
    unrespondedOnly: options.unrespondedOnly?.toString(),
    humanOnly: options.humanOnly?.toString()
  };
  Object.entries(paramMap).forEach(([key, value]) => {
    if (value !== undefined) {
      searchParams.set(key, value);
    }
  });
  return await requestData<CursorPage<ChatSummary>>(`comms/chat/list?${searchParams.toString()}`, "get", {
    signal: options.controller?.signal
  }, false);
}
async function fetchMessages(chatId: string): Promise<ChatDetail | null> {
  return await requestData<ChatDetail>("comms/chat/get", "get", {
    searchParams: {
      chatId
    }
  }, false);
}
async function fetchChatFromPatient(patientId: string): Promise<ChatDetail | null> {
  return await requestData<ChatDetail>("comms/chat/get", "get", {
    searchParams: {
      patient_id: patientId
    }
  });
}
export async function fetchVoiceToken(): Promise<{
  token: string;
  // Backend-generated Call id. The browser sets this as the `call_id`
  // userVariable before dialing so the Call row is deterministic, and uses it
  // as the key for the in-call "save visit as note" scribe-intent toggle.
  call_id: string;
} | null> {
  return await requestData<{
    token: string;
    call_id: string;
  }>("comms/call/token", "post");
}

/**
 * The clinic number (E.164) a call to this patient should come from: the
 * default number of the patient's location, else the clinic's main number.
 * Dialing waits on this, so it gives up fast and the caller falls back to the
 * main number rather than holding the call for the default 20s timeout.
 */
export async function fetchOutboundCallerId(patientId: string): Promise<string | null> {
  const response = await requestData<{
    phoneNumber: string;
  }>("comms/call/caller-id", "get", {
    searchParams: {
      patient_id: patientId
    },
    retry: 0
  }, true, 2000);
  return response?.phoneNumber ?? null;
}
export interface CallScribeIntentResponse {
  call_id: string;
  enabled: boolean;
  note_template_id: string | null;
}

/**
 * Set or clear the in-call "save visit as note" intent for a call. Safe to call
 * repeatedly as the provider toggles the switch or changes the template
 * mid-call; the backend upserts by call id and does not require the Call row to
 * exist yet (outbound browser calls toggle this before the recording webhook).
 *
 * Passes `catchError: false` so a failed request rejects (instead of resolving
 * to `null`); the caller's optimistic-update rollback depends on the real error
 * propagating rather than a silent false success.
 */
export async function setCallScribeIntent(callId: string, enabled: boolean, noteTemplateId: string | null): Promise<CallScribeIntentResponse | null> {
  return await requestData<CallScribeIntentResponse>(`comms/call/${callId}/scribe-intent`, "post", {
    json: {
      enabled,
      note_template_id: noteTemplateId
    }
  }, false);
}
async function sendMessage(message: FormData): Promise<MessageSummary | null> {
  return await requestData<MessageSummary>("comms/chat/send-message", "post", {
    body: message
  }, false);
}
async function scheduleMessage(body: {
  chat_id: string;
  sender_id: string;
  content: string;
  send_at: string;
}): Promise<ScheduledMessageSummary | null> {
  return await requestData<ScheduledMessageSummary>("comms/chat/schedule-message", "post", {
    json: body
  }, false);
}
async function fetchScheduledMessages(chatId: string): Promise<ScheduledMessageSummary[]> {
  const response = await requestData<ScheduledMessageSummary[]>("comms/chat/scheduled-messages", "get", {
    searchParams: {
      chat_id: chatId
    }
  });
  return response ?? [];
}
async function cancelScheduledMessage(id: string): Promise<ScheduledMessageSummary | null> {
  return await requestData<ScheduledMessageSummary>(`comms/chat/scheduled-messages/${id}/cancel`, "post", {}, false);
}
async function updateChatStatus(chatId: string, status: ChatStatus): Promise<null> {
  return await requestData(`comms/chat/${chatId}/${status}`, "put", {});
}
interface MarkAllChatsReadResult {
  chatIds: string[];
  unreadCounts: UnreadCounts;
}
async function markAllChatsRead(locationId?: string, chatIds?: string[]): Promise<MarkAllChatsReadResult> {
  const result = await requestData<MarkAllChatsReadResult>("comms/chat/mark-all-read", "post", {
    searchParams: {
      locationId
    },
    ...(chatIds && {
      json: {
        chatIds
      }
    })
  }, false);
  if (!result) throw new Error("Mark all chats read returned no result");
  return result;
}
async function updateChatMode(chatId: string, mode: ChatMode): Promise<ChatMode | null> {
  return await requestData<ChatMode>(`comms/chat/${chatId}/mode`, "put", {
    searchParams: {
      mode
    }
  });
}
async function refreshSignedUrl(signedUrl: string, filename: string | null = null): Promise<string | null> {
  return await requestData<string>(`tenant/refresh-signed-url`, "get", {
    searchParams: {
      signedUrl,
      filename
    }
  });
}
async function optOutFromTexting(chatId: string, status: boolean = true): Promise<boolean | null> {
  return await requestData<boolean>(`comms/chat/${chatId}/opt-out`, "put", {
    searchParams: {
      status
    }
  });
}
async function optOutFromAutomatedMessages(patientId: string): Promise<boolean | null> {
  return await requestData<boolean>(`comms/chat/${patientId}/opt-out-from-automated-messages`, "put", {}, false);
}
async function optInFromAutomatedMessages(patientId: string): Promise<boolean | null> {
  return await requestData<boolean>(`comms/chat/${patientId}/opt-in-from-automated-messages`, "put", {}, false);
}
async function getOptOutFromAutomatedMessages(patientId: string): Promise<boolean | null> {
  return await requestData<boolean>(`comms/chat/${patientId}/opt-out-from-automated-messages`, "get", {}, false);
}
export function useSmsOptOutStatusForPatient(patientId) {
  const $ = _c(6);
  let t0;
  if ($[0] !== patientId) {
    t0 = chatQueryOptions.fromPatient(patientId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      ...t0,
      select: _temp
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data
  } = useQuery(t1);
  let t2;
  if ($[4] !== data) {
    t2 = data ?? {
      clinicOptedOut: false,
      patientOptedOut: false
    };
    $[4] = data;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  return t2;
}
function _temp(chat) {
  return {
    clinicOptedOut: chat?.clinicOptedOutOfSms ?? false,
    patientOptedOut: chat?.patientOptedOutOfSms ?? false
  };
}
export const useUpdateClinicOptOutStatusForPatient = patientId => {
  const $ = _c(12);
  let t0;
  if ($[0] !== patientId) {
    t0 = chatQueryOptions.fromPatient(patientId);
    $[0] = patientId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t0) {
    t1 = {
      ...t0,
      select: _temp2
    };
    $[2] = t0;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    data: chatDetail
  } = useQuery(t1);
  const optOutFromTextingMutation = useOptOutFromTextingMutation();
  let t2;
  if ($[4] !== chatDetail?.id || $[5] !== optOutFromTextingMutation || $[6] !== patientId) {
    t2 = async status => {
      const fromPatientKey = chatQueryKeys.fromPatient(patientId);
      await queryClient.cancelQueries({
        queryKey: fromPatientKey
      });
      const prev = queryClient.getQueryData(fromPatientKey);
      if (prev) {
        queryClient.setQueryData(fromPatientKey, {
          ...prev,
          clinicOptedOutOfSms: status
        });
      }
      optOutFromTextingMutation.mutate({
        chatId: chatDetail?.id ?? "",
        status
      }, {
        onError: () => {
          if (prev) {
            queryClient.setQueryData(fromPatientKey, prev);
          }
        }
      });
    };
    $[4] = chatDetail?.id;
    $[5] = optOutFromTextingMutation;
    $[6] = patientId;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== optOutFromTextingMutation.isError || $[9] !== optOutFromTextingMutation.isPending || $[10] !== t2) {
    t3 = {
      isPending: optOutFromTextingMutation.isPending,
      isError: optOutFromTextingMutation.isError,
      mutate: t2
    };
    $[8] = optOutFromTextingMutation.isError;
    $[9] = optOutFromTextingMutation.isPending;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
};
async function updatePrimaryPatient(chatId: string, patientId: string): Promise<boolean | null> {
  return await requestData<boolean>(`comms/chat/${chatId}/primary-patient/${patientId}`, "put", {}, false);
}
async function regenerateAIText(feedback: string, suggestedMessageId: string): Promise<null> {
  return await requestData(`comms/chat/regenerate-suggestion`, "post", {
    json: {
      feedback,
      suggestedMessageId
    }
  });
}
async function draftTaskFromMessage({
  chatId,
  messageId
}: {
  chatId: string;
  messageId: string;
}): Promise<ChatTaskDraft | null> {
  return await requestData<ChatTaskDraft>("comms/chat/draft-task", "post", {
    json: {
      chatId,
      messageId
    }
  }, false, 30000);
}
async function sendSuggestedMessage(suggestedMessageId: string): Promise<null> {
  return await requestData(`comms/chat/send-suggested-message/${suggestedMessageId}`, "put", {});
}
async function dismissSuggestedMessage(suggestedMessageId: string): Promise<null> {
  return await requestData(`comms/chat/dismiss-suggested-message/${suggestedMessageId}`, "put", {});
}
async function executeToolCall(toolCallId: string): Promise<null> {
  return await requestData(`comms/chat/execute-tool-call`, "get", {
    searchParams: {
      toolCallId
    }
  }, false);
}
export const chatQueryKeys = {
  all: ["comms", "chat"] as const,
  unreadCount: (locationId?: string) => [...chatQueryKeys.all, "unread-count", locationId] as const,
  list: (options?: FetchChatsOptions) => [...chatQueryKeys.all, "list", options] as const,
  infiniteList: (options?: Omit<FetchChatsOptions, "cursor" | "perPage">) => {
    const baseKey: any[] = [...chatQueryKeys.all, "infinite-list"];
    if (options) {
      baseKey.push(options);
    }
    return baseKey;
  },
  detail: (chatId?: string) => [...chatQueryKeys.all, "detail", chatId] as const,
  fromPatient: (patientId: string) => [...chatQueryKeys.all, "from-patient", patientId] as const,
  optOutStatus: (patientId: string) => [...chatQueryKeys.all, "opt-out-status", patientId] as const
};

/**
 * Invalidate every unread-count query (global + all location-filtered variants)
 * so they refetch from the server. Used to resync on websocket (re)connect,
 * where a client may have missed CHAT_UPDATE count pushes while disconnected.
 * Drops the trailing `undefined` slot so the key prefix-matches all variants.
 */
export const invalidateChatUnreadCount = () => queryClient.invalidateQueries({
  queryKey: chatQueryKeys.unreadCount().filter(part => part !== undefined)
});
export const chatQueryOptions = {
  unreadCount: (locationId?: string) => ({
    queryKey: chatQueryKeys.unreadCount(locationId),
    queryFn: () => fetchUnreadCount(locationId),
    staleTime: 30 * 1000 // 30 seconds - reduces redundant refetches across sidebar, menu, messaging
  }),
  infiniteList: (options?: Omit<FetchChatsOptions, "cursor" | "perPage">, perPage: number = 12) => ({
    queryKey: chatQueryKeys.infiniteList(options),
    queryFn: ({
      pageParam = null
    }: {
      pageParam?: string | null;
    }) => fetchChats({
      ...options,
      cursor: pageParam,
      perPage
    }),
    getNextPageParam: (lastPage: CursorPage<ChatSummary> | null) => lastPage?.nextCursor ?? undefined,
    initialPageParam: null,
    refetchOnWindowFocus: true
  }),
  detail: (chatId: string) => ({
    queryKey: chatQueryKeys.detail(chatId),
    queryFn: () => fetchMessages(chatId),
    refetchOnWindowFocus: true
  }),
  fromPatient: (patientId: string) => ({
    queryKey: chatQueryKeys.fromPatient(patientId),
    queryFn: async () => {
      const res = await fetchChatFromPatient(patientId);
      if (!res) return null;
      queryClient.setQueryData(chatQueryKeys.detail(res.id), res);
      return res;
    }
  }),
  optOutStatus: (patientId: string) => ({
    queryKey: chatQueryKeys.optOutStatus(patientId),
    queryFn: () => getOptOutFromAutomatedMessages(patientId)
  })
};

/**
 * Helper functions for optimistic updates
 */
type ChatDetailUpdater = (chat: ChatDetail) => ChatDetail;
type ChatSummaryUpdater = (chat: ChatSummary) => ChatSummary;

/**
 * Cancels queries for a specific chat
 */
const cancelChatQueries = async (chatId: string) => {
  await queryClient.cancelQueries({
    queryKey: chatQueryKeys.detail(chatId)
  });
  await queryClient.cancelQueries({
    queryKey: chatQueryKeys.infiniteList()
  });
};

/**
 * Updates the ChatDetail query optimistically
 */
export const updateChatDetail = (chatId: string, updater: ChatDetailUpdater): ChatDetail | undefined => {
  const detailKey = chatQueryKeys.detail(chatId);
  const previousData = queryClient.getQueryData<ChatDetail>(detailKey);
  if (previousData) {
    const updated = updater(previousData);
    queryClient.setQueryData<ChatDetail>(detailKey, updated);
  }
  return previousData;
};

/**
 * Checks if a chat exists in any infinite list query
 */
const chatExistsInInfiniteList = (chatId: string): boolean => {
  const infiniteListQueries = queryClient.getQueriesData<InfiniteData<CursorPage<ChatSummary>>>({
    queryKey: chatQueryKeys.infiniteList()
  });
  for (const [, data] of infiniteListQueries) {
    if (!data) continue;
    for (const page of data.pages) {
      if (!page?.data) continue;
      if (page.data.some(chat => chat.id === chatId)) {
        return true;
      }
    }
  }
  return false;
};

/**
 * Options for filter matching context that can't be inferred from the chat itself
 */
interface FilterMatchOptions {
  /** Whether the latest message is inbound (from patient/external). Used for unrespondedOnly filter. */
  isInboundMessage?: boolean;
  /** Whether the latest message was sent by an AI assistant. Used for humanOnly filter. */
  isFromAssistant?: boolean;
}

/**
 * Checks if a chat matches the given filters (mirrors backend logic)
 */
const chatMatchesFilters = (chat: ChatBase, filters: Omit<FetchChatsOptions, "cursor" | "perPage">, options?: FilterMatchOptions): boolean => {
  // Filter: unreadOnly
  if (filters.unreadOnly && chat.status !== ChatStatus.UNREAD) {
    return false;
  }

  // Filter: locationId
  if (filters.locationId) {
    const hasMatchingLocation = chat.patients.some(patient => patient.primaryLocationId === filters.locationId);
    if (!hasMatchingLocation) {
      return false;
    }
  }

  // Filter: humanOnly — mirrors backend logic:
  // 1. Exclude opted-out chats
  // 2. Exclude chats whose latest message is from an AI assistant
  if (filters.humanOnly) {
    if (isOptedOutOfSms(chat)) {
      return false;
    }
    if (options?.isFromAssistant) {
      return false;
    }
  }

  // Filter: unrespondedOnly - only include if explicitly inbound
  if (filters.unrespondedOnly) {
    if (options?.isInboundMessage !== true) {
      return false;
    }
  }

  // Filter: searchTerm (requires patient search, complex to check client-side)
  // We'll be lenient and let backend filter it

  return true;
};

/**
 * Updates a chat in all infinite list queries
 * Returns a map of query keys to their previous data for rollback purposes
 */
export const updateChatInInfiniteList = (chatId: string, updater: ChatSummaryUpdater): Map<readonly unknown[], InfiniteData<CursorPage<ChatSummary>>> => {
  const infiniteListQueries = queryClient.getQueriesData<InfiniteData<CursorPage<ChatSummary>>>({
    queryKey: chatQueryKeys.infiniteList()
  });
  const previousDataMap = new Map<readonly unknown[], InfiniteData<CursorPage<ChatSummary>>>();
  for (const [queryKey, data] of infiniteListQueries) {
    if (data) {
      // Store previous data for rollback
      previousDataMap.set(queryKey, data);
      queryClient.setQueryData(queryKey, {
        ...data,
        pages: data.pages.map(page => ({
          ...page,
          data: page.data.map(chat => chat.id === chatId ? updater(chat) : chat)
        }))
      });
    }
  }
  return previousDataMap;
};

/**
 * Removes a chat from cached infinite list queries whose filter its new state
 * no longer satisfies.
 *
 * Mutations that change filter-affecting state (status, opt-out, latest-message
 * direction) used to reconcile filter membership by invalidating the list and
 * letting the refetch return the authoritative filtered set. That relied on the
 * refetch hitting the primary. `/comms/chat/list` now reads the replica, so an
 * invalidate can return the pre-mutation state and leave the chat visible in
 * views its new state no longer belongs to (e.g. a chat marked READ lingering
 * in the unreadOnly filter until the user refreshes).
 *
 * Callers pass a predicate over the query's filter options that returns true
 * when the chat should be REMOVED from that cache entry. The optimistic update
 * in `onMutate` already patched the chat's fields; this helper just prunes
 * cache entries whose filter the patched state no longer matches.
 */
export const pruneChatFromInfiniteLists = (chatId: string, shouldRemove: (options: Omit<FetchChatsOptions, "cursor" | "perPage">) => boolean): void => {
  const infiniteListQueries = queryClient.getQueriesData<InfiniteData<CursorPage<ChatSummary>>>({
    queryKey: chatQueryKeys.infiniteList()
  });
  for (const [queryKey, data] of infiniteListQueries) {
    if (!data) continue;
    const options = queryKey[3] as Omit<FetchChatsOptions, "cursor" | "perPage"> | undefined ?? {};
    if (!shouldRemove(options)) continue;
    queryClient.setQueryData(queryKey, {
      ...data,
      pages: data.pages.map(page => ({
        ...page,
        data: page.data.filter(c => c.id !== chatId)
      }))
    });
  }
};

/**
 * Rolls back the ChatDetail query to previous data
 */
export const rollbackChatDetail = (chatId: string, previousData: ChatDetail | undefined) => {
  if (previousData === undefined) return;
  const detailKey = chatQueryKeys.detail(chatId);
  queryClient.setQueryData<ChatDetail | undefined>(detailKey, previousData);
};

/**
 * Rolls back infinite list queries to previous data
 */
export const rollbackChatInInfiniteList = (previousDataMap: Map<readonly unknown[], InfiniteData<CursorPage<ChatSummary>>>) => {
  previousDataMap.forEach((previousData, queryKey) => {
    queryClient.setQueryData(queryKey, previousData);
  });
};
const deduplicateMessages = (messages: (CallDetail | MessageSummary)[]): (CallDetail | MessageSummary)[] => {
  const seenMessageUids = new Set<string>();
  const seenIds = new Set<string>();
  const result: (CallDetail | MessageSummary)[] = [];
  if (messages.length === 0) {
    return [];
  }
  for (const message of [...messages].reverse()) {
    // For MessageSummary with messageUid, deduplicate by messageUid
    if ("messageUid" in message && message.messageUid) {
      if (!seenMessageUids.has(message.messageUid)) {
        seenMessageUids.add(message.messageUid);
        result.unshift(message);
      }
    } else {
      // Otherwise, deduplicate by id (for CallDetail or MessageSummary without messageUid)
      if (!seenIds.has(message.id)) {
        seenIds.add(message.id);
        result.unshift(message);
      }
    }
  }
  return result;
};

/**
 * Updates or adds an email to an array of emails.
 * If an email with the same id exists, it will be updated.
 * Otherwise, the email will be added and sorted by createdDate.
 *
 * @param emails - Array of emails to update
 * @param newEmail - Email to add or update
 * @returns Updated array with the email added/updated and sorted by createdDate
 */
export function updateOrAddEmail(emails: ChatEmailSummary[], newEmail: ChatEmailSummary): ChatEmailSummary[] {
  const existingIndex = emails.findIndex(e => e.id === newEmail.id);
  if (existingIndex >= 0) {
    // Update existing email
    return emails.map((e, i) => i === existingIndex ? newEmail : e);
  }

  // Add new email and sort by createdDate
  return [...emails, newEmail].sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
}

/**
 * Updates or adds a message to an array of messages.
 * If a message with the same id exists, it will be updated.
 * Otherwise, the message will be added and the array will be sorted by createdDate.
 *
 * @param messages - Array of messages to update
 * @param newMessage - Message to add or update
 * @returns Updated array with the message added/updated and sorted by createdDate
 */
export function updateOrAddMessage(messages: (CallDetail | MessageSummary)[], newMessage: CallDetail | MessageSummary): (CallDetail | MessageSummary)[] {
  const existingIndex = messages.findIndex(m => m.id === newMessage.id);
  if (existingIndex >= 0) {
    // Update existing message
    return messages.map((m, i) => i === existingIndex ? newMessage : m);
  }

  // Add new message and sort by createdDate
  return [...messages, newMessage].sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime());
}

/**
 * Handles prepending a new chat to infinite lists if it doesn't exist
 * and matches the filters. Uses the provided ChatSummary directly.
 * Checks all infinite list queries and prepends to matching ones.
 * Optionally updates the chat detail cache with the provided message.
 */
export const prependNewChatToInfiniteLists = (chat: ChatBase, message?: MessageSummary | CallDetail, options?: FilterMatchOptions): void => {
  // Skip if chat already exists in any infinite list
  if (chatExistsInInfiniteList(chat.id)) {
    return;
  }

  // Optionally update chat detail cache with the message
  if (message) {
    const existingDetail = queryClient.getQueryData<ChatDetail>(chatQueryKeys.detail(chat.id));
    if (existingDetail) {
      // Add message to existing detail
      const updatedMessages = updateOrAddMessage(existingDetail.messages, message);
      queryClient.setQueryData<ChatDetail>(chatQueryKeys.detail(chat.id), {
        ...existingDetail,
        messages: deduplicateMessages(updatedMessages),
        status: chat.status,
        patientOptedOutOfSms: chat.patientOptedOutOfSms,
        clinicOptedOutOfSms: chat.clinicOptedOutOfSms,
        patients: chat.patients,
        primaryPatientId: chat.primaryPatientId
      });
    }
  }

  // Get all infinite list queries and prepend to matching ones
  const infiniteListQueries = queryClient.getQueriesData<InfiniteData<CursorPage<ChatSummary>>>({
    queryKey: chatQueryKeys.infiniteList()
  });
  for (const [queryKey, data] of infiniteListQueries) {
    if (!data) continue;

    // Extract filters from query key
    const queryKeyFilters = queryKey[3] as Omit<FetchChatsOptions, "cursor" | "perPage"> | undefined;

    // Use empty filters if not present (default query)
    const filters: Omit<FetchChatsOptions, "cursor" | "perPage"> = queryKeyFilters ?? {};

    // Check if chat matches filters
    if (!chatMatchesFilters(chat, filters, options)) {
      continue;
    }

    // Check if chat already exists in this query
    const chatExists = data.pages.some(page => page.data.some(c => c.id === chat.id));
    if (chatExists) {
      continue;
    }

    // Prepend to first page
    const firstPage = data.pages[0];
    if (firstPage) {
      queryClient.setQueryData(queryKey, {
        ...data,
        pages: [{
          ...firstPage,
          data: [chat, ...firstPage.data],
          total: (firstPage.total ?? 0) + 1
        }, ...data.pages.slice(1)]
      });
    }
  }
};

/**
 * Updates both ChatDetail and infinite list queries optimistically
 * Returns both previous data for rollback
 */
export const updateChatOptimistically = (chatId: string, detailUpdater: ChatDetailUpdater, summaryUpdater: ChatSummaryUpdater): {
  previousDetail: ChatDetail | undefined;
  previousInfiniteListData: Map<readonly unknown[], InfiniteData<CursorPage<ChatSummary>>>;
} => {
  const previousDetail = updateChatDetail(chatId, chat => {
    const updated = detailUpdater(chat);
    return {
      ...updated,
      messages: deduplicateMessages(updated.messages)
    };
  });
  const previousInfiniteListData = updateChatInInfiniteList(chatId, summaryUpdater);
  return {
    previousDetail,
    previousInfiniteListData
  };
};
export interface SendMessageInput {
  message: MessageContext;
  chatId: string;
  content: string;
  files?: File[];
  /** Channel the composer displayed; applied atomically with the send. */
  mode?: ChatMode;
  /** Patient a portal message is for, as the composer showed it. */
  recipientPatientId?: string;
  messageUid?: string;
  clinicPhoneNumberId?: string;
}
export const useSendMessageMutation = () => {
  const $ = _c(10);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] !== currentUser?.id) {
    t0 = input => {
      const formData = new FormData();
      const messageUid = input.messageUid ?? createId("msg");
      formData.append("message_uid", messageUid);
      formData.append("content", input.content);
      formData.append("sender_id", currentUser?.id ?? "");
      formData.append("status", "CREATED");
      formData.append("chat_id", input.chatId);
      const context = input.message;
      formData.append("message", JSON.stringify(context.kind === MessageKind.WAITLIST ? {
        kind: context.kind,
        waitlist_entry_ids: context.waitlistEntryIds
      } : context.kind === MessageKind.EVENT ? {
        kind: context.kind,
        event_id: context.eventId,
        message_type: context.messageType,
        quote_id: context.quoteId
      } : {
        kind: context.kind,
        quote_id: context.quoteId
      }));
      if (input.clinicPhoneNumberId) {
        formData.append("clinic_phone_number_id", input.clinicPhoneNumberId);
      }
      if (input.mode) {
        formData.append("mode", input.mode);
      }
      if (input.recipientPatientId) {
        formData.append("recipient_patient_id", input.recipientPatientId);
      }
      input.files?.forEach(file => formData.append("files", file));
      return sendMessage(formData);
    };
    $[0] = currentUser?.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== currentUser?.firstName || $[3] !== currentUser?.id || $[4] !== currentUser?.lastName || $[5] !== currentUser?.type) {
    t1 = async input_0 => {
      const messageUid_0 = input_0.messageUid ?? createId("msg");
      const newMessage = {
        id: messageUid_0,
        messageUid: messageUid_0,
        content: input_0.content,
        createdDate: new Date().toISOString(),
        status: MessageStatus.CREATED,
        sender: {
          id: currentUser?.id ?? "",
          firstName: currentUser?.firstName ?? "",
          lastName: currentUser?.lastName ?? "",
          type: currentUser?.type ?? UserType.PROVIDER
        },
        chatId: input_0.chatId,
        eventId: input_0.message.kind === MessageKind.EVENT ? input_0.message.eventId : undefined,
        type: getContextMessageType(input_0.message),
        clinicPhoneNumberId: input_0.clinicPhoneNumberId,
        files: input_0.files?.map(_temp3) ?? []
      } satisfies MessageSummary;
      const {
        previousDetail,
        previousInfiniteListData
      } = updateChatOptimistically(input_0.chatId, chat => ({
        ...chat,
        messages: updateOrAddMessage(chat.messages, newMessage)
      }), chat_0 => ({
        ...chat_0,
        latestMessageContent: newMessage.content,
        latestMessageDate: newMessage.createdDate
      }));
      return {
        previousDetail,
        previousInfiniteListData,
        chatId: input_0.chatId
      };
    };
    $[2] = currentUser?.firstName;
    $[3] = currentUser?.id;
    $[4] = currentUser?.lastName;
    $[5] = currentUser?.type;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== t0 || $[8] !== t1) {
    t2 = {
      mutationFn: t0,
      onMutate: t1,
      onSuccess: _temp4,
      onError: _temp5
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  return useMutation(t2);
};
export const scheduledMessagesQueryKey = (chatId: string) => ["scheduledMessages", chatId] as const;
export const useScheduledMessages = chatId => {
  const $ = _c(8);
  const t0 = chatId ?? "";
  let t1;
  if ($[0] !== t0) {
    t1 = scheduledMessagesQueryKey(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== chatId) {
    t2 = () => fetchScheduledMessages(chatId ?? "");
    $[2] = chatId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = !!chatId;
  let t4;
  if ($[4] !== t1 || $[5] !== t2 || $[6] !== t3) {
    t4 = {
      queryKey: t1,
      queryFn: t2,
      enabled: t3
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return useQuery(t4);
};
export const useScheduleMessageMutation = () => {
  const $ = _c(2);
  const {
    currentUser
  } = useUser();
  let t0;
  if ($[0] !== currentUser?.id) {
    t0 = {
      mutationFn: input => scheduleMessage({
        chat_id: input.chatId,
        sender_id: currentUser?.id ?? "",
        content: input.content,
        send_at: input.sendAt.toISOString()
      }),
      onSuccess: _temp6
    };
    $[0] = currentUser?.id;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};
export const useCancelScheduledMessageMutation = chatId => {
  const $ = _c(2);
  let t0;
  if ($[0] !== chatId) {
    t0 = {
      mutationFn: _temp7,
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: scheduledMessagesQueryKey(chatId)
        });
      }
    };
    $[0] = chatId;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return useMutation(t0);
};

/**
 * Marks every unread chat READ, or only those at `locationId` (the sidebar's
 * "My Location" filter). Patches the caches from the server's list of chat ids
 * rather than refetching, since `/comms/chat/list` reads the replica and could
 * still return them as UNREAD.
 */
export const useMarkAllChatsReadMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp8,
      onSuccess: _temp10
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdateChatStatusMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp11,
      onMutate: _temp12,
      onError: _temp13,
      onSuccess: _temp14
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdateChatModeMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp15,
      onMutate: _temp16,
      onError: _temp17
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useOptOutFromTextingMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp18,
      onMutate: _temp19,
      onError: _temp20
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useOptOutFromAutomatedMessagesMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp21,
      onSuccess: _temp22
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useOptInFromAutomatedMessagesMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp23,
      onSuccess: _temp24
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useUpdatePrimaryPatientMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp25,
      onMutate: _temp26,
      onError: _temp27
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useRegenerateAITextMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp28
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useDraftTaskFromMessageMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp29
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useSendSuggestedMessageMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp30,
      onSuccess: _temp32
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useDismissSuggestedMessageMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp33
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useExecuteToolCallMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp34,
      onSuccess: _temp35
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
export const useRefreshSignedUrlMutation = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      mutationFn: _temp36
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return useMutation(t0);
};
function _temp2(data) {
  return data;
}
function _temp3() {
  return "temp";
}
function _temp4(response, input_1) {
  if (!response) {
    return;
  }
  invalidateOpportunityContactViews();
  updateChatOptimistically(input_1.chatId, chat_1 => ({
    ...chat_1,
    messages: updateOrAddMessage(chat_1.messages, response)
  }), chat_2 => ({
    ...chat_2,
    latestMessageContent: response.content ?? "",
    latestMessageDate: response.createdDate
  }));
}
function _temp5(_error, _input, context_0) {
  if (!context_0) {
    return;
  }
  rollbackChatDetail(context_0.chatId, context_0.previousDetail);
  rollbackChatInInfiniteList(context_0.previousInfiniteListData);
}
function _temp6(_response, input_0) {
  queryClient.invalidateQueries({
    queryKey: scheduledMessagesQueryKey(input_0.chatId)
  });
}
function _temp7(scheduledMessageId) {
  return cancelScheduledMessage(scheduledMessageId);
}
function _temp8(t0) {
  const {
    locationId,
    chatIds
  } = t0;
  return markAllChatsRead(locationId, chatIds);
}
function _temp9(chat_1) {
  return chat_1 ? {
    ...chat_1,
    status: ChatStatus.READ
  } : chat_1;
}
function _temp10(t0) {
  const {
    chatIds: chatIds_0,
    unreadCounts
  } = t0;
  const markedRead = new Set(chatIds_0);
  const infiniteListQueries = queryClient.getQueriesData({
    queryKey: chatQueryKeys.infiniteList()
  });
  for (const [queryKey, data] of infiniteListQueries) {
    if (!data) {
      continue;
    }
    const options = queryKey[3] as Omit<FetchChatsOptions, "cursor" | "perPage"> | undefined ?? {};
    queryClient.setQueryData(queryKey, {
      ...data,
      pages: data.pages.map(page => ({
        ...page,
        data: page.data.filter(chat => !(options.unreadOnly && markedRead.has(chat.id))).map(chat_0 => markedRead.has(chat_0.id) ? {
          ...chat_0,
          status: ChatStatus.READ
        } : chat_0)
      }))
    });
  }
  for (const chatId of chatIds_0) {
    queryClient.setQueryData(chatQueryKeys.detail(chatId), _temp9);
  }
  queryClient.setQueryData(chatQueryKeys.unreadCount(undefined), unreadCounts.total);
  for (const [locationId_0, count] of Object.entries(unreadCounts.byLocation)) {
    queryClient.setQueryData(chatQueryKeys.unreadCount(locationId_0), count);
  }
}
function _temp11(t0) {
  const {
    chatId,
    status
  } = t0;
  return updateChatStatus(chatId, status);
}
async function _temp12(t0) {
  const {
    chatId: chatId_0,
    status: status_0
  } = t0;
  await cancelChatQueries(chatId_0);
  const {
    previousDetail,
    previousInfiniteListData
  } = updateChatOptimistically(chatId_0, chat => ({
    ...chat,
    status: status_0
  }), chat_0 => ({
    ...chat_0,
    status: status_0
  }));
  return {
    previousDetail,
    previousInfiniteListData
  };
}
function _temp13(_error, _variables, context) {
  if (!context) {
    return;
  }
  rollbackChatDetail(_variables.chatId, context.previousDetail);
  rollbackChatInInfiniteList(context.previousInfiniteListData);
}
function _temp14(_data, t0) {
  const {
    chatId: chatId_1,
    status: status_1
  } = t0;
  pruneChatFromInfiniteLists(chatId_1, options => options.unreadOnly === true && status_1 !== ChatStatus.UNREAD);
}
function _temp15(t0) {
  const {
    chatId,
    mode
  } = t0;
  return updateChatMode(chatId, mode);
}
async function _temp16(t0) {
  const {
    chatId: chatId_0,
    mode: mode_0
  } = t0;
  await cancelChatQueries(chatId_0);
  const {
    previousDetail,
    previousInfiniteListData
  } = updateChatOptimistically(chatId_0, chat => ({
    ...chat,
    mode: mode_0
  }), chat_0 => ({
    ...chat_0,
    mode: mode_0
  }));
  return {
    previousDetail,
    previousInfiniteListData
  };
}
function _temp17(_error, _variables, context) {
  if (!context) {
    return;
  }
  rollbackChatDetail(_variables.chatId, context.previousDetail);
  rollbackChatInInfiniteList(context.previousInfiniteListData);
}
function _temp18(t0) {
  const {
    chatId,
    status
  } = t0;
  return optOutFromTexting(chatId, status);
}
async function _temp19(t0) {
  const {
    chatId: chatId_0,
    status: status_0
  } = t0;
  await cancelChatQueries(chatId_0);
  const newClinicStatus = status_0 ?? true;
  const {
    previousDetail,
    previousInfiniteListData
  } = updateChatOptimistically(chatId_0, chat => ({
    ...chat,
    clinicOptedOutOfSms: newClinicStatus
  }), chat_0 => ({
    ...chat_0,
    clinicOptedOutOfSms: newClinicStatus
  }));
  return {
    previousDetail,
    previousInfiniteListData
  };
}
function _temp20(_error, _variables, context) {
  if (!context) {
    return;
  }
  rollbackChatDetail(_variables.chatId, context.previousDetail);
  rollbackChatInInfiniteList(context.previousInfiniteListData);
}
function _temp21(patientId) {
  return optOutFromAutomatedMessages(patientId);
}
function _temp22(_, patientId_0) {
  queryClient.invalidateQueries({
    queryKey: chatQueryKeys.optOutStatus(patientId_0)
  });
  queryClient.invalidateQueries({
    queryKey: chatQueryKeys.fromPatient(patientId_0)
  });
}
function _temp23(patientId) {
  return optInFromAutomatedMessages(patientId);
}
function _temp24(_, patientId_0) {
  queryClient.invalidateQueries({
    queryKey: chatQueryKeys.optOutStatus(patientId_0)
  });
  queryClient.invalidateQueries({
    queryKey: chatQueryKeys.fromPatient(patientId_0)
  });
}
function _temp25(t0) {
  const {
    chatId,
    patientId
  } = t0;
  return updatePrimaryPatient(chatId, patientId);
}
async function _temp26(t0) {
  const {
    chatId: chatId_0,
    patientId: patientId_0
  } = t0;
  await cancelChatQueries(chatId_0);
  const {
    previousDetail,
    previousInfiniteListData
  } = updateChatOptimistically(chatId_0, chat => ({
    ...chat,
    primaryPatientId: patientId_0
  }), chat_0 => ({
    ...chat_0,
    primaryPatientId: patientId_0
  }));
  return {
    previousDetail,
    previousInfiniteListData
  };
}
function _temp27(_error, _variables, context) {
  if (!context) {
    return;
  }
  rollbackChatDetail(_variables.chatId, context.previousDetail);
  rollbackChatInInfiniteList(context.previousInfiniteListData);
}
function _temp28(t0) {
  const {
    feedback,
    suggestedMessageId
  } = t0;
  return regenerateAIText(feedback, suggestedMessageId);
}
function _temp29(input) {
  return draftTaskFromMessage(input);
}
function _temp30(t0) {
  const {
    suggestedMessageId
  } = t0;
  return sendSuggestedMessage(suggestedMessageId);
}
function _temp31(options) {
  return options.unrespondedOnly === true || options.humanOnly === true;
}
function _temp32(_, t0) {
  const {
    chatId
  } = t0;
  queryClient.invalidateQueries({
    queryKey: chatQueryKeys.detail(chatId)
  });
  pruneChatFromInfiniteLists(chatId, _temp31);
}
function _temp33(suggestedMessageId) {
  return dismissSuggestedMessage(suggestedMessageId);
}
function _temp34(t0) {
  const {
    toolCallId
  } = t0;
  return executeToolCall(toolCallId);
}
function _temp35(_, t0) {
  const {
    chatId
  } = t0;
  queryClient.invalidateQueries({
    queryKey: chatQueryKeys.detail(chatId)
  });
}
function _temp36(t0) {
  const {
    signedUrl,
    filename
  } = t0;
  return refreshSignedUrl(signedUrl, filename ?? null);
}